/**
 * RuntimeContextCollector — singleton that monkey-patches browser APIs to capture
 * runtime context for the debug chatbot.
 *
 * Lifecycle:
 *   1. Instantiate via `new RuntimeContextCollector()` (or use the singleton exported below)
 *   2. It immediately patches console, fetch, and window events
 *   3. Call `destroy()` to restore all originals
 */

import {
  ConsoleLogEntry,
  NetworkRequestEntry,
  WebSocketEvent,
  ErrorBoundaryEvent,
  PerformanceEntry,
  RuntimeContext,
  AppStateSnapshot,
  EnvironmentInfo,
  BUFFER_SIZES,
  SERIALIZATION_DEPTH,
  MAX_ARG_LENGTH,
  MAX_BODY_PREVIEW_LENGTH,
} from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Push to a fixed-size circular buffer.
 * When the buffer is full, the oldest entry is dropped.
 */
function circularPush<T>(buffer: T[], item: T, maxSize: number): void {
  if (buffer.length >= maxSize) {
    buffer.shift();
  }
  buffer.push(item);
}

/**
 * Safely serialize a value to a JSON string with depth and length limits.
 * Handles circular references and non-serializable values.
 */
function safeSerialize(value: unknown, maxDepth: number = SERIALIZATION_DEPTH): string {
  const seen = new WeakSet<object>();

  function replacer(depth: number) {
    return function (_key: string, val: unknown): unknown {
      if (val === null || typeof val !== 'object') {
        return val;
      }
      if (depth >= maxDepth) {
        return '[...]';
      }
      if (seen.has(val as object)) {
        return '[Circular]';
      }
      seen.add(val as object);
      // Return a new object with each property recursively replaced
      if (Array.isArray(val)) {
        const result: unknown[] = [];
        for (const item of val) {
          result.push(applyReplacer(item, depth + 1));
        }
        // Clean up so other branches don't falsely detect as seen
        seen.delete(val as object);
        return result;
      }
      const result: Record<string, unknown> = {};
      for (const k of Object.keys(val as object)) {
        result[k] = applyReplacer((val as Record<string, unknown>)[k], depth + 1);
      }
      seen.delete(val as object);
      return result;
    };
  }

  function applyReplacer(val: unknown, depth: number): unknown {
    if (val === null || typeof val !== 'object') {
      return val;
    }
    if (depth >= maxDepth) {
      return '[...]';
    }
    if (seen.has(val as object)) {
      return '[Circular]';
    }
    seen.add(val as object);
    let result: unknown;
    if (Array.isArray(val)) {
      result = (val as unknown[]).map((item) => applyReplacer(item, depth + 1));
    } else {
      const obj: Record<string, unknown> = {};
      for (const k of Object.keys(val as object)) {
        obj[k] = applyReplacer((val as Record<string, unknown>)[k], depth + 1);
      }
      result = obj;
    }
    seen.delete(val as object);
    return result;
  }

  try {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'undefined') {
      return 'undefined';
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'bigint') {
      return `${value.toString()}n`;
    }
    if (typeof value === 'symbol') {
      return String(value);
    }
    if (typeof value === 'function') {
      return `[Function ${value.name || 'anonymous'}]`;
    }
    if (value instanceof Error) {
      return `${value.name}: ${value.message}`;
    }
    const processed = applyReplacer(value, 0);
    const serialized = JSON.stringify(processed, replacer(0));
    return serialized ?? String(processed);
  } catch {
    return '[UnserializableValue]';
  }
}

/**
 * Truncate a string to maxLength, appending '...' if truncated.
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
}

// ---------------------------------------------------------------------------
// RuntimeContextCollector
// ---------------------------------------------------------------------------

export class RuntimeContextCollector {
  private consoleLogs: ConsoleLogEntry[] = [];
  private networkRequests: NetworkRequestEntry[] = [];
  private wsEvents: WebSocketEvent[] = [];
  private errors: ErrorBoundaryEvent[] = [];
  private performance: PerformanceEntry[] = [];

  // Originals for restore
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    error: typeof console.error;
    info: typeof console.info;
    debug: typeof console.debug;
  };
  private originalFetch: typeof globalThis.fetch;
  private errorHandler: (event: Event) => void;
  private rejectionHandler: (event: Event) => void;
  private perfObserver?: InstanceType<typeof PerformanceObserver>;

  constructor() {
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };
    this.originalFetch = (typeof window !== 'undefined' ? (window as any).fetch : undefined)
      ?? (typeof globalThis !== 'undefined' ? (globalThis as any).fetch : undefined);

    this.errorHandler = this.handleWindowError.bind(this);
    this.rejectionHandler = this.handleUnhandledRejection.bind(this);

    this.patchConsole();
    this.patchFetch();
    this.attachWindowListeners();
    this.setupPerformanceObserver();
  }

  // --- Console patching ---

  private patchConsole(): void {
    const self = this;

    const levels = ['log', 'warn', 'error', 'info', 'debug'] as const;
    for (const level of levels) {
      const original = this.originalConsole[level];
      console[level] = (...args: unknown[]) => {
        // Call original first so the browser still shows the log
        original(...args);
        self.captureConsoleEntry(level, args);
      };
    }
  }

  private captureConsoleEntry(
    level: ConsoleLogEntry['level'],
    args: unknown[],
  ): void {
    const serializedArgs = args.map((arg) => {
      const str = safeSerialize(arg);
      return truncate(str, MAX_ARG_LENGTH);
    });

    const entry: ConsoleLogEntry = {
      timestamp: Date.now(),
      level,
      args: serializedArgs,
    };

    circularPush(this.consoleLogs, entry, BUFFER_SIZES.consoleLogs);
  }

  // --- Fetch patching ---

  private patchFetch(): void {
    const self = this;
    const original = this.originalFetch;

    const patched = async function (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const startTime = Date.now();
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
      const method = init?.method ?? (input instanceof Request ? input.method : 'GET');

      // Capture request body preview
      let requestBodyPreview: string | undefined;
      if (init?.body) {
        try {
          const bodyStr = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
          requestBodyPreview = truncate(bodyStr, MAX_BODY_PREVIEW_LENGTH);
        } catch {
          // ignore
        }
      }

      try {
        const response = await original.call(
          typeof window !== 'undefined' ? window : globalThis,
          input,
          init,
        );
        const durationMs = Date.now() - startTime;

        // Only capture failed requests
        if (response.status >= 400) {
          let responseBodyPreview: string | undefined;
          try {
            const cloned = response.clone();
            const bodyText = await cloned.text();
            responseBodyPreview = truncate(bodyText, MAX_BODY_PREVIEW_LENGTH);
          } catch {
            // ignore
          }

          const entry: NetworkRequestEntry = {
            timestamp: startTime,
            method: method.toUpperCase(),
            url,
            status: response.status,
            durationMs,
            requestBodyPreview,
            responseBodyPreview,
          };
          circularPush(self.networkRequests, entry, BUFFER_SIZES.networkRequests);
        }

        return response;
      } catch (err: unknown) {
        const durationMs = Date.now() - startTime;

        const entry: NetworkRequestEntry = {
          timestamp: startTime,
          method: method.toUpperCase(),
          url,
          status: null,
          durationMs,
          error: err instanceof Error ? err.message : String(err),
          requestBodyPreview,
        };
        circularPush(self.networkRequests, entry, BUFFER_SIZES.networkRequests);

        throw err;
      }
    };

    // Assign to both global fetch and window.fetch
    (globalThis as any).fetch = patched;
    if (typeof window !== 'undefined') {
      (window as any).fetch = patched;
    }
  }

  // --- Window event listeners ---

  private attachWindowListeners(): void {
    const target = typeof window !== 'undefined' ? window : (globalThis as any);
    if (target?.addEventListener) {
      target.addEventListener('error', this.errorHandler);
      target.addEventListener('unhandledrejection', this.rejectionHandler);
    }
  }

  private handleWindowError(event: Event): void {
    const errorEvent = event as ErrorEvent;
    const entry: ErrorBoundaryEvent = {
      timestamp: Date.now(),
      message: errorEvent.message ?? String(errorEvent),
      stack: errorEvent.error?.stack,
    };
    circularPush(this.errors, entry, BUFFER_SIZES.errors);
  }

  private handleUnhandledRejection(event: Event): void {
    const rejEvent = event as PromiseRejectionEvent;
    const reason = rejEvent.reason;
    const entry: ErrorBoundaryEvent = {
      timestamp: Date.now(),
      message: reason instanceof Error ? reason.message : String(reason ?? 'Unhandled rejection'),
      stack: reason instanceof Error ? reason.stack : undefined,
    };
    circularPush(this.errors, entry, BUFFER_SIZES.errors);
  }

  // --- PerformanceObserver ---

  private setupPerformanceObserver(): void {
    if (typeof PerformanceObserver === 'undefined') return;

    try {
      this.perfObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'longtask' && entry.duration > 50) {
            const perfEntry: PerformanceEntry = {
              timestamp: Date.now(),
              type: 'long_task',
              durationMs: Math.round(entry.duration),
            };
            circularPush(this.performance, perfEntry, BUFFER_SIZES.performance);
          }
        }
      });
      this.perfObserver.observe({ entryTypes: ['longtask'] });
    } catch {
      // PerformanceObserver not supported in this environment
    }
  }

  // --- Public API ---

  /**
   * Report a WebSocket event explicitly.
   */
  reportWsEvent(event: WebSocketEvent): void {
    circularPush(this.wsEvents, event, BUFFER_SIZES.wsEvents);
  }

  /**
   * Report an error from an error boundary or other catch site.
   */
  reportError(
    error: Error,
    componentStack?: string,
    sentryEventId?: string,
  ): void {
    const entry: ErrorBoundaryEvent = {
      timestamp: Date.now(),
      message: error.message,
      stack: error.stack,
      componentStack,
      sentryEventId,
    };
    circularPush(this.errors, entry, BUFFER_SIZES.errors);
  }

  /**
   * Snapshot the current runtime context.
   * Returns copies of all buffers so the caller cannot mutate internal state.
   */
  getContext(): RuntimeContext {
    const appState: AppStateSnapshot = {
      wsConnectionStatus: 'unknown',
      currentRoute:
        typeof window !== 'undefined' ? window.location?.pathname ?? '/' : '/',
      walletConnected: false,
      visibleDialogs: [],
      screenSize: {
        width: typeof window !== 'undefined' ? window.innerWidth ?? 0 : 0,
        height: typeof window !== 'undefined' ? window.innerHeight ?? 0 : 0,
      },
      timestamp: Date.now(),
    };

    const environment: EnvironmentInfo = {
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      buildId:
        typeof process !== 'undefined'
          ? (process.env?.NEXT_PUBLIC_DEV_VERSION ?? 'unknown')
          : 'unknown',
      dataEnv:
        typeof process !== 'undefined'
          ? (process.env?.NEXT_PUBLIC_DATA_ENV ?? 'unknown')
          : 'unknown',
      nextPublicApiUrl:
        typeof process !== 'undefined'
          ? (process.env?.NEXT_PUBLIC_VERTEX_API_URL ?? '')
          : '',
      sentryEnabled:
        (typeof process !== 'undefined' &&
          process.env?.NEXT_PUBLIC_SENTRY_DSN !== undefined) ??
        false,
      timestamp: Date.now(),
    };

    return {
      consoleLogs: [...this.consoleLogs],
      networkRequests: [...this.networkRequests],
      wsEvents: [...this.wsEvents],
      errors: [...this.errors],
      performance: [...this.performance],
      appState,
      environment,
    };
  }

  /**
   * Restore all monkey-patched APIs and remove event listeners.
   */
  destroy(): void {
    // Restore console
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;

    // Restore fetch
    if (this.originalFetch) {
      (globalThis as any).fetch = this.originalFetch;
      if (typeof window !== 'undefined') {
        (window as any).fetch = this.originalFetch;
      }
    }

    // Remove window listeners
    const target = typeof window !== 'undefined' ? window : (globalThis as any);
    if (target?.removeEventListener) {
      target.removeEventListener('error', this.errorHandler);
      target.removeEventListener('unhandledrejection', this.rejectionHandler);
    }

    // Disconnect PerformanceObserver
    this.perfObserver?.disconnect();
  }
}
